import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";

export default function MarketExperience() {
  const { t } = useI18n();
  const logos = t("experience.logos");

  return (
    <section
      data-testid={TID.experience}
      className="relative bg-[color:var(--gc-paper)] py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 gc-grid-bg opacity-50 pointer-events-none" />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="gc-eyebrow">{t("experience.eyebrow")}</p>
            <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-[700] text-[color:var(--gc-ink)]">
              {t("experience.title")}
            </h2>
            <div className="mt-8 h-px w-24 bg-[color:var(--gc-red)]" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <p className="text-lg md:text-xl leading-relaxed text-[color:var(--gc-ink)] font-display font-[500]">
              {t("experience.subtitle")}
            </p>
            <div className="mt-8 space-y-5">
              <p className="text-base lg:text-[17px] leading-relaxed text-[color:var(--gc-graphite)]">
                {t("experience.p1")}
              </p>
              <p className="text-base lg:text-[17px] leading-relaxed text-[color:var(--gc-graphite)]">
                {t("experience.p2")}
              </p>
              <p className="text-base lg:text-[17px] leading-relaxed text-[color:var(--gc-graphite)]">
                {t("experience.p3")}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Logo grid panel */}
        <div className="mt-20">
          <div className="flex items-center justify-between mb-6">
            <p className="gc-eyebrow text-[color:var(--gc-fog)]">
              {t("experience.grid_label")}
            </p>
            <span className="hidden sm:block flex-1 mx-6 h-px bg-[color:var(--gc-line)]" />
            <span className="font-mono text-[11px] tracking-[0.18em] text-[color:var(--gc-mist)]">
              {Array.isArray(logos) ? String(logos.length).padStart(2, "0") : "00"}
            </span>
          </div>

          <div
            data-testid="experience-logos-grid"
            className="relative bg-[color:var(--gc-navy)] overflow-hidden"
          >
            <div className="absolute inset-0 gc-grid-bg-dark opacity-40 pointer-events-none" />
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
              {Array.isArray(logos) &&
                logos.map((logo, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                    data-testid={`experience-logo-slot-${i + 1}`}
                    className="relative bg-[color:var(--gc-navy)] h-36 md:h-40 lg:h-44 flex items-center justify-center p-6 lg:p-8 group"
                    title={logo.name}
                  >
                    <span className="font-mono text-[10px] tracking-[0.18em] text-white/30 absolute top-3 left-3">
                      /{String(i + 1).padStart(2, "0")}
                    </span>
                    <img
                      src={logo.src}
                      alt={logo.name}
                      loading="lazy"
                      draggable="false"
                      className="max-h-[55%] max-w-[80%] w-auto h-auto object-contain select-none opacity-55 group-hover:opacity-90 transition-opacity duration-500"
                    />
                    <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/0 group-hover:text-white/45 absolute bottom-3 right-4 transition-colors duration-500">
                      {logo.name}
                    </span>
                  </motion.div>
                ))}
            </div>
          </div>

          <p className="mt-8 text-xs md:text-[13px] leading-relaxed text-[color:var(--gc-fog)] max-w-4xl italic">
            {t("experience.disclaimer")}
          </p>
        </div>
      </div>
    </section>
  );
}
